// Exercise ledger drawer for one optional task — the per-exercise history the
// SDS asks for ("each time the task is exercised: how much, at what cost, with
// a description"), plus the terms that make one exercised instance legible on
// its own: its SLIN, quantity, funded amount, and period of performance.
// Ordered by SLIN here rather than by the server's exercised_at, because that
// is the order the PM reads them in. Recording or deleting an exercise also
// flips the parent task's derived `exercised` flag server-side, so both
// queries invalidate together.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Label, SpecraButton, SpecraCheckboxField, SpecraDateField, SpecraInput, SpecraOverlayDrawer, SpecraSectionHeader, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DocumentViewerDrawer } from '../../../../components/ProposalDashboard/DocumentViewer';
import { useIsViewOnly } from '../../../../contexts/authContext';
import { useArtifactNames } from '../../../../hooks/useDeliveryDocuments';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { programRef } from '../../../../lib/access/types';
import { deliveryClient } from '../../../../lib/connect/clients';
import { formatContractDate } from '../../../../lib/contractDate';
import { ExpandablePwsChips, PwsChips, parsePwsSections, usePwsSectionRefs } from '../../_shared/components/PwsChips';
import { useSourceRefsForTarget } from '../../_shared/hooks/lineageAuditHooks';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { lineNowEnds, reexerciseNotes } from '../../_shared/lib/exerciseTranches';
import { deriveOptionalTaskLifecycle, OPTIONAL_TASK_BADGE_LABEL, OPTIONAL_TASK_TAB_LABEL } from '../../_shared/lib/optionalTaskLifecycle';
import { presentOptionalTaskTitle } from '../../_shared/lib/optionalTaskTitleCase';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { parseSpan } from '../../_shared/lib/sourceSpan';
import { CM_KEY } from '../../Deliverables/hooks';
import { useProgram } from '../hooks';
import { ALL_OPTIONAL_TASK_EXERCISES_KEY } from '../operationalHooks';
import { useProgramWriteAccess } from '../programWriteAccess';
import { useProgramModificationChangeSets } from '../v2/modificationHooks';
const EXERCISES_KEY = 'optional task exercises';
const TASKS_KEY = 'optional task'; // makeEntityHooks list key — parent flag refresh
// 0 renders as "—": proto doubles have no null, so 0 doubles as "not
// captured" (a genuinely free exercise also reads as — ; acceptable until the
// wire grows optionality). Exported so OptionalTasksTab's scoped Total column
// renders the same not-captured affordance instead of a fabricated $0
// (SAD-341) — the per-exercise total value is never wired up from the
// "Record exercise" form, so a scoped sum of quantity*unitPrice is routinely
// 0 while cost/funded is real.
export const money = (v)=>v > 0 ? formatMoney(v) : '—';
const qty = (v)=>v.toLocaleString(undefined, {
        maximumFractionDigits: 2
    });
const dateStr = (ts)=>ts ? ts.toDate().toISOString().slice(0, 10) : '—';
// SAD-340 — Matt's complaint was that a rate/labor-category table embedded in
// the free-text description (OMEGA's real rate-heavy descriptions run
// ~1000 chars) pushes the Task/Total value/Funded/PWS/CLIN facts off screen.
// There's no reliable marker phrase to truncate at (that was the option the
// ticket explicitly rejected as contract-specific and brittle), so this
// collapses by length instead: OMEGA's own ordinary descriptions run ~110-120
// chars (one sentence), so 240 — two full sentences' worth — comfortably
// clears them with no affordance at all, while still catching the rate-table
// descriptions well before their labor-category listing starts.
const DESCRIPTION_COLLAPSE_THRESHOLD = 240;
// Cuts on a word boundary rather than mid-word/mid-number (a rate table's
// "$121.53" truncated to "$121." reads as a typo, not an intentional clip).
function truncateDescription(text, limit) {
    const cut = text.slice(0, limit);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
// A blank date field means "not captured", which the wire represents as an
// absent Timestamp — never as the epoch.
const dateOrUndefined = (value)=>value ? Timestamp.fromDate(new Date(`${value}T00:00:00Z`)) : undefined;
// Exercises read back in SLIN order (5001-1, 5001-2, 5001-10), which is how
// the PM scans them — not in the exercised_at order the server lists them in,
// and not in the order they happened to be entered. Numeric collation puts
// 5001-2 before 5001-10; exercises with no SLIN sort last rather than
// jumping the queue on an empty string, and id breaks any remaining tie so
// the order is stable across renders.
const slinCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base'
});
// Shape shared by OptionalTaskExerciseProto and OptionalTaskExerciseSlinProto
// (SAD-151) — both carry their own clin_number/id, so the same comparison
// orders either an exercise ledger or one exercise's itemised contributors.
function byExerciseSlin(a, b) {
    if (!a.clinNumber !== !b.clinNumber) return a.clinNumber ? -1 : 1;
    const bySlin = slinCollator.compare(a.clinNumber, b.clinNumber);
    if (bySlin !== 0) return bySlin;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
export function OptionalTaskExercisesDrawer({ programId, task, onClose, scopeTab, referenceDate }) {
    const canWriteExecution = useProgramWriteAccess(programId).execution;
    const isViewOnly = useIsViewOnly() || !canWriteExecution;
    const qc = useQueryClient();
    // Backing zou project — lets the Source Texts line items open the document viewer on the ref's page.
    const { data: chipProg } = useProgram(programId);
    const chipProjectId = chipProg?.program?.zouProjectId ?? '';
    const { data, isLoading } = useQuery({
        queryKey: [
            EXERCISES_KEY,
            task.id
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listOptionalTaskExercises({
                programId,
                optionalTaskId: task.id
            });
            return resp.exercises;
        }
    });
    const lifecycle = useMemo(()=>deriveOptionalTaskLifecycle(data ?? [], referenceDate ?? new Date()), [
        data,
        referenceDate
    ]);
    // A later tranche of an as-exercised window names the modification whose
    // window it re-exercises (K9c-1a).
    const notes = useMemo(()=>reexerciseNotes(data ?? []), [
        data
    ]);
    const { data: changeSets } = useProgramModificationChangeSets(programId, useTenantId());
    const modTitle = (changeSetId)=>changeSets?.find((cs)=>cs.changeSetId === changeSetId)?.title || 'an earlier modification';
    // Only Exercised/Completed are real scopes to recompute down to — All and
    // Not-exercised (and no scope at all) show the task's full, unscoped picture.
    const isScoped = scopeTab === 'exercised' || scopeTab === 'completed';
    // Display only — stored `task.name` is never rewritten (SAD-338). The
    // requirement-text section below intentionally keeps the raw name as its
    // fallback: that's source/requirement text, not a title label (and is
    // SAD-339's surface to rework).
    const displayTitle = presentOptionalTaskTitle(task.name);
    // SAD-340 — collapsed by default so a rate-heavy description doesn't push
    // the facts above it off screen; the full text is always one click/keypress
    // away, never dropped.
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const descriptionNeedsCollapse = task.description.length > DESCRIPTION_COLLAPSE_THRESHOLD;
    const descriptionId = `optional-task-description-${task.id}`;
    const [slin, setSlin] = useState('');
    const [qtyInput, setQtyInput] = useState('');
    const [funded, setFunded] = useState('');
    const [unitPriceInput, setUnitPriceInput] = useState('');
    const [when, setWhen] = useState('');
    const [popStart, setPopStart] = useState('');
    const [popEnd, setPopEnd] = useState('');
    const [note, setNote] = useState('');
    const [editing, setEditing] = useState();
    // SAD-822: a mod-applied exercise's lines each state their own window, and
    // the schedule reads a line's window before the exercise's. A changed
    // header PoP goes onto every line unless the PM unticks this.
    const [applyPopToLines, setApplyPopToLines] = useState(true);
    const [editingLine, setEditingLine] = useState();
    const [lineStart, setLineStart] = useState('');
    const [lineEnd, setLineEnd] = useState('');
    const editingLineCount = editing?.contributingSlins?.length ?? 0;
    const headerPopChanged = !!editing && (popStart !== (editing.popStartDate ? dateStr(editing.popStartDate) : '') || popEnd !== (editing.popEndDate ? dateStr(editing.popEndDate) : ''));
    const offerApplyToLines = headerPopChanged && editingLineCount > 0;
    const lineRangeInvalid = lineStart !== '' && lineEnd !== '' && lineEnd < lineStart;
    // QTY/Funded/Unit price all declare `min="0"` in their markup, but that
    // attribute is cosmetic on a plain number input — nothing stops a typed
    // "-5000" from reaching the mutation. ryugu's CreateOptionalTaskExercise
    // only checks that program_id/optional_task_id are non-empty and forwards
    // the numbers straight through to pangaea, so the client is the only place
    // this is ever caught (server-side validation is out of scope: a hard
    // reject there would also govern modification-driven writes). Blank stays
    // valid — the mutation already treats it as "not captured" and sends 0.
    const hasNegativeField = [
        qtyInput,
        funded,
        unitPriceInput
    ].some((v)=>v !== '' && Number(v) < 0);
    const sortedExercises = useMemo(()=>[
            ...data ?? []
        ].sort(byExerciseSlin), [
        data
    ]);
    // Scoped down to only the rows that qualify for scopeTab (SAD-341) — 'all'
    // when unscoped, which is exactly sortedExercises and must never change.
    const scopedExercises = useMemo(()=>isScoped ? [
            ...lifecycle.rowsForTab(scopeTab)
        ].sort(byExerciseSlin) : sortedExercises, [
        isScoped,
        scopeTab,
        lifecycle,
        sortedExercises
    ]);
    const scopedTotalValue = isScoped ? scopedExercises.reduce((sum, e)=>sum + e.quantity * e.unitPrice, 0) : task.totalAmount;
    // Unscoped Funded mirrors OptionalTasksTab's All/Not-exercised rule
    // (SAD-341, superseding Matt's earlier "does not change"): sum `cost`
    // across every one of the task's exercise rows once it has at least one,
    // rather than trusting the stored `fundedAmount` that nothing ever updates
    // when a modification exercises a task. Zero exercises leaves nothing to
    // sum, so the stored amount stands — same fallback the table uses, so the
    // table and this drawer can never disagree for the same task.
    const scopedFundedValue = isScoped ? scopedExercises.reduce((sum, e)=>sum + e.cost, 0) : sortedExercises.length > 0 ? sortedExercises.reduce((sum, e)=>sum + e.cost, 0) : task.fundedAmount;
    const historyLabel = isScoped ? OPTIONAL_TASK_TAB_LABEL[scopeTab] : OPTIONAL_TASK_BADGE_LABEL[lifecycle.badgeStatus];
    // Source Texts' contract-PDF line item prefers the exercising SLIN's own
    // clin_number (narrower — the specific SLIN the exercise happened under)
    // over the task's own CLIN, falling back to the latter when no exercise
    // carries one. Always keyed off the FULL exercise list — Source Texts is
    // not part of the tab-scoped surface.
    const sourceClinNumber = sortedExercises.find((e)=>e.clinNumber)?.clinNumber || task.clinNumber;
    const pwsSections = parsePwsSections(task.pwsSections);
    const pwsSectionRefs = usePwsSectionRefs({
        sections: task.pwsSections,
        programId
    });
    // Fail-open guarantee: when neither Source Texts line item resolves any
    // provenance, the drawer must still show *something* rather than nothing —
    // requirement text (or the task name) is the last resort, not the default.
    // "Resolves" means a real, openable citation — the same
    // `!!projectId && !!ref?.sourceArtifactId` predicate PwsSectionChip uses for
    // canOpen — never just that pws_sections has entries. A task can carry
    // section labels for which extraction never wrote (or hasn't yet written) a
    // PwsObligation ref; those render as inert badges, not source text, so they
    // must not suppress the fallback.
    const [contractResolved, setContractResolved] = useState(false);
    const pwsResolved = !!chipProjectId && pwsSections.some((s)=>!!pwsSectionRefs.get(s)?.sourceArtifactId);
    const hasAnySourceText = contractResolved || pwsResolved;
    const invalidate = ()=>{
        qc.invalidateQueries({
            queryKey: [
                EXERCISES_KEY,
                task.id
            ]
        });
        qc.invalidateQueries({
            queryKey: [
                TASKS_KEY
            ]
        });
        // The OptionalTasksTab's derived tab membership is fed by every task's
        // exercises fetched under this key — a recorded/deleted exercise here
        // must invalidate it too, or a task's tab membership goes stale.
        qc.invalidateQueries({
            queryKey: [
                ALL_OPTIONAL_TASK_EXERCISES_KEY
            ]
        });
        // Recording or removing an exercise mints or retires its deliverable
        // dates in the same transaction (D112 ruling 3).
        qc.invalidateQueries({
            queryKey: [
                CM_KEY
            ]
        });
    };
    const resetForm = ()=>{
        setSlin('');
        setQtyInput('');
        setFunded('');
        setUnitPriceInput('');
        setWhen('');
        setPopStart('');
        setPopEnd('');
        setNote('');
        setEditing(undefined);
        setApplyPopToLines(true);
    };
    const save = useMutation({
        mutationFn: async ()=>{
            // Belt and braces: the button disables on a negative field, but a
            // programmatic .mutate() call would skip that gate. Refusing to fire
            // is the right failure mode here, not clamping — silently rewriting a
            // typed -5000 into 0 would fabricate a value the PM never entered.
            if (hasNegativeField) {
                throw new Error('Quantity, funded amount and unit price cannot be negative.');
            }
            const values = {
                programId,
                // QTY writes `quantity` — the field the row's "Qty" already reads.
                // amount_exercised is deliberately left unset: each exercise is its
                // own row now, so the separate "Amount" figure it fed is redundant.
                quantity: Number(qtyInput) || 0,
                cost: Number(funded) || 0,
                unitPrice: Number(unitPriceInput) || 0,
                clinNumber: slin.trim(),
                description: note,
                exercisedAt: dateOrUndefined(when),
                popStartDate: dateOrUndefined(popStart),
                popEndDate: dateOrUndefined(popEnd)
            };
            return editing ? deliveryClient.updateOptionalTaskExercise({
                ...values,
                id: editing.id,
                amountExercised: editing.amountExercised,
                applyPopToLines: offerApplyToLines && applyPopToLines
            }) : deliveryClient.createOptionalTaskExercise({
                ...values,
                optionalTaskId: task.id
            });
        },
        onSuccess: ()=>{
            toast.success(editing ? 'Exercise updated' : 'Exercise recorded');
            resetForm();
            invalidate();
        },
        onError: (err)=>toast.error(err instanceof Error ? err.message : 'Failed to save exercise')
    });
    const saveLine = useMutation({
        mutationFn: async ()=>{
            if (!editingLine) throw new Error('No line is being edited.');
            return deliveryClient.updateOptionalTaskExerciseLine({
                id: editingLine.id,
                exerciseId: editingLine.exerciseId,
                programId,
                popStartDate: dateOrUndefined(lineStart),
                popEndDate: dateOrUndefined(lineEnd)
            });
        },
        onSuccess: ()=>{
            toast.success('Line period updated');
            setEditingLine(undefined);
            invalidate();
        },
        onError: (err)=>toast.error(err instanceof Error ? err.message : 'Failed to update the line period')
    });
    const editLine = (exerciseId, line)=>{
        setEditingLine({
            id: line.id,
            exerciseId
        });
        setLineStart(line.popStartDate ? dateStr(line.popStartDate) : '');
        setLineEnd(line.popEndDate ? dateStr(line.popEndDate) : '');
    };
    const edit = (exercise)=>{
        setEditing(exercise);
        setApplyPopToLines(true);
        setSlin(exercise.clinNumber);
        setQtyInput(exercise.quantity ? String(exercise.quantity) : '');
        setFunded(exercise.cost ? String(exercise.cost) : '');
        setUnitPriceInput(exercise.unitPrice ? String(exercise.unitPrice) : '');
        setWhen(exercise.exercisedAt ? dateStr(exercise.exercisedAt) : '');
        setPopStart(exercise.popStartDate ? dateStr(exercise.popStartDate) : '');
        setPopEnd(exercise.popEndDate ? dateStr(exercise.popEndDate) : '');
        setNote(exercise.description);
    };
    const remove = useMutation({
        mutationFn: async (id)=>deliveryClient.deleteOptionalTaskExercise({
                id,
                programId
            }),
        onSuccess: ()=>{
            toast.success('Exercise deleted');
            invalidate();
        },
        onError: (err)=>toast.error(err instanceof Error ? err.message : 'Failed to delete exercise')
    });
    return /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
        open: true,
        onOpenChange: (next)=>{
            if (!next) onClose();
        },
        title: displayTitle,
        testId: Testids.optionalTasks.exercisesDrawer,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between items-center",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "Task"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "inline-flex items-center font-medium",
                                children: displayTitle
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "Total value"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                "data-testid": Testids.optionalTasks.scopedTotalValue,
                                children: /*#__PURE__*/ _jsx(MaskedField, {
                                    fieldName: "total_amount",
                                    resource: programRef(programId),
                                    children: money(scopedTotalValue)
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "Funded"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": Testids.optionalTasks.scopedFundedValue,
                                children: /*#__PURE__*/ _jsx(MaskedField, {
                                    fieldName: "funded_amount",
                                    resource: programRef(programId),
                                    children: money(scopedFundedValue)
                                })
                            })
                        ]
                    }),
                    isScoped && /*#__PURE__*/ _jsxs("p", {
                        className: "pt-0.5 text-xs text-muted-foreground",
                        children: [
                            "Scoped to the ",
                            historyLabel,
                            " view — ",
                            scopedExercises.length,
                            " of ",
                            sortedExercises.length,
                            ' ',
                            "exercise",
                            sortedExercises.length === 1 ? '' : 's',
                            "."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between items-center",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "PWS"
                            }),
                            /*#__PURE__*/ _jsx(PwsChips, {
                                sections: task.pwsSections
                            })
                        ]
                    }),
                    task.clinNumber && /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                children: "CLIN"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-mono",
                                children: [
                                    task.clinNumber,
                                    task.pricingType ? ` · ${task.pricingType}` : ''
                                ]
                            })
                        ]
                    }),
                    task.description && /*#__PURE__*/ _jsxs("div", {
                        className: "pt-1",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                id: descriptionId,
                                className: "whitespace-pre-wrap text-muted-foreground",
                                "data-testid": Testids.optionalTasks.descriptionText,
                                children: descriptionNeedsCollapse && !descriptionExpanded ? truncateDescription(task.description, DESCRIPTION_COLLAPSE_THRESHOLD) : task.description
                            }),
                            descriptionNeedsCollapse && /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                className: "mt-1 font-medium text-primary hover:underline",
                                "aria-expanded": descriptionExpanded,
                                "aria-controls": descriptionId,
                                onClick: ()=>setDescriptionExpanded((expanded)=>!expanded),
                                "data-testid": Testids.optionalTasks.descriptionToggle,
                                children: descriptionExpanded ? 'Show less' : 'Show more'
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                "data-testid": Testids.optionalTasks.sourceTextsSection,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Source Texts"
                    }),
                    /*#__PURE__*/ _jsxs("ul", {
                        className: "mt-2 space-y-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(ContractSourceLineItem, {
                                    programId: programId,
                                    projectId: chipProjectId,
                                    clinNumber: sourceClinNumber,
                                    onResolvedChange: setContractResolved
                                })
                            }),
                            /*#__PURE__*/ _jsxs("li", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-muted-foreground",
                                        children: [
                                            "PWS section",
                                            pwsSections.length > 1 ? 's' : ''
                                        ]
                                    }),
                                    pwsSections.length > 0 ? /*#__PURE__*/ _jsx(ExpandablePwsChips, {
                                        sections: task.pwsSections,
                                        programId: programId,
                                        projectId: chipProjectId
                                    }) : /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        "data-testid": Testids.optionalTasks.sourceTextUnavailable,
                                        children: "No source location on file."
                                    })
                                ]
                            }),
                            !hasAnySourceText && /*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx("p", {
                                    className: "text-muted-foreground",
                                    "data-testid": Testids.optionalTasks.sourceTextFallback,
                                    children: task.requirementText || task.name
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: `Exercise history — ${historyLabel.toLowerCase()}`,
                        "data-testid": Testids.optionalTasks.exerciseHistoryHeading
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2",
                        children: [
                            isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Loading…"
                            }),
                            !isLoading && scopedExercises.length === 0 && /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                "data-testid": Testids.optionalTasks.exerciseHistoryEmpty,
                                children: isScoped && sortedExercises.length > 0 ? `No exercises are currently ${historyLabel.toLowerCase()} for this task.` : 'No exercises recorded. Each exercise captures how much was exercised, at what cost, and when.'
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-2",
                                children: scopedExercises.map((e)=>{
                                    // SAD-151: a modification can apply many SLINs against this
                                    // task; the backend still collapses them to one exercise row,
                                    // but contributingSlins now carries each SLIN's own terms. A
                                    // row with contributors is a modification-level event covering
                                    // N SLINs, not a single SLIN — the header and money must say
                                    // so, and each contributor gets its own itemised line. A
                                    // manually recorded exercise (or one applied before this
                                    // migration existed) carries no contributors and renders
                                    // exactly as before.
                                    const contributors = [
                                        ...e.contributingSlins ?? []
                                    ].sort(byExerciseSlin);
                                    const hasContributors = contributors.length > 0;
                                    return /*#__PURE__*/ _jsxs("li", {
                                        className: "rounded-md border p-2 text-sm",
                                        "data-testid": Testids.optionalTasks.exerciseRow,
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-mono font-medium",
                                                        "data-testid": Testids.optionalTasks.exerciseRowSlin,
                                                        children: hasContributors ? `Exercised via modification — ${contributors.length} SLIN${contributors.length === 1 ? '' : 's'}` : `SLIN ${e.clinNumber || '—'}${e.pricingType ? ` · ${e.pricingType}` : ''}`
                                                    }),
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        children: [
                                                            hasContributors && /*#__PURE__*/ _jsx("span", {
                                                                className: "mr-1 text-xs font-normal text-muted-foreground",
                                                                children: "Modification total"
                                                            }),
                                                            money(e.cost)
                                                        ]
                                                    }),
                                                    !isViewOnly && /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center gap-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                                variant: "ghost",
                                                                size: "icon",
                                                                "aria-label": "Edit exercise",
                                                                "data-testid": Testids.optionalTasks.editExercise,
                                                                onClick: ()=>edit(e),
                                                                children: /*#__PURE__*/ _jsx(Pencil, {
                                                                    className: "h-3.5 w-3.5"
                                                                })
                                                            }),
                                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                                variant: "ghost",
                                                                size: "icon",
                                                                "aria-label": "Delete exercise",
                                                                onClick: ()=>remove.mutate(e.id),
                                                                children: /*#__PURE__*/ _jsx(Trash2, {
                                                                    className: "h-3.5 w-3.5"
                                                                })
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            notes.has(e.id) && /*#__PURE__*/ _jsxs("div", {
                                                className: "mt-1 text-xs text-muted-foreground",
                                                "data-testid": Testids.optionalTasks.exerciseReexercise,
                                                children: [
                                                    "Re-exercise of ",
                                                    modTitle(notes.get(e.id)?.of.changeSetId ?? ''),
                                                    "'s window",
                                                    notes.get(e.id)?.afterWindowEnded ? ', after the window ended' : ''
                                                ]
                                            }),
                                            hasContributors && /*#__PURE__*/ _jsxs("div", {
                                                className: "mt-1 grid grid-cols-2 gap-x-3 text-xs text-muted-foreground",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        children: [
                                                            "Exercised ",
                                                            dateStr(e.exercisedAt)
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        children: [
                                                            "Period ",
                                                            dateStr(e.popStartDate),
                                                            " – ",
                                                            dateStr(e.popEndDate)
                                                        ]
                                                    })
                                                ]
                                            }),
                                            hasContributors ? /*#__PURE__*/ _jsx("ul", {
                                                className: "mt-2 space-y-2 divide-y divide-border/40",
                                                children: contributors.map((c)=>/*#__PURE__*/ _jsxs("li", {
                                                        className: "pt-2 first:pt-0",
                                                        "data-testid": Testids.optionalTasks.exerciseContributorRow,
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "flex items-center justify-between gap-2",
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs("span", {
                                                                        className: "font-mono font-medium",
                                                                        children: [
                                                                            "SLIN ",
                                                                            c.clinNumber || '—',
                                                                            c.pricingType ? ` · ${c.pricingType}` : ''
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("span", {
                                                                        className: "flex items-center gap-1",
                                                                        children: [
                                                                            money(c.fundedAmount),
                                                                            !isViewOnly && /*#__PURE__*/ _jsx(SpecraButton, {
                                                                                variant: "ghost",
                                                                                size: "icon",
                                                                                "aria-label": c.clinNumber ? `Edit SLIN ${c.clinNumber} period` : 'Edit line period',
                                                                                "data-testid": Testids.optionalTasks.editExerciseLine,
                                                                                onClick: ()=>editLine(e.id, c),
                                                                                children: /*#__PURE__*/ _jsx(Pencil, {
                                                                                    className: "h-3.5 w-3.5"
                                                                                })
                                                                            })
                                                                        ]
                                                                    })
                                                                ]
                                                            }),
                                                            editingLine?.id === c.id && /*#__PURE__*/ _jsxs("div", {
                                                                className: "mt-2 space-y-2",
                                                                "data-testid": Testids.optionalTasks.exerciseLineForm,
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx(SpecraDateField, {
                                                                                id: `line-pop-start-${c.id}`,
                                                                                label: "Line period start",
                                                                                value: lineStart,
                                                                                onValueChange: setLineStart
                                                                            }),
                                                                            /*#__PURE__*/ _jsx(SpecraDateField, {
                                                                                id: `line-pop-end-${c.id}`,
                                                                                label: "Line period end",
                                                                                value: lineEnd,
                                                                                onValueChange: setLineEnd,
                                                                                error: lineRangeInvalid ? 'The end cannot be before the start.' : undefined
                                                                            })
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("div", {
                                                                        className: "flex items-center gap-2",
                                                                        children: [
                                                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                                                size: "sm",
                                                                                onClick: ()=>saveLine.mutate(),
                                                                                disabled: saveLine.isPending || lineRangeInvalid,
                                                                                "data-testid": Testids.optionalTasks.saveExerciseLine,
                                                                                children: saveLine.isPending ? 'Saving…' : 'Save'
                                                                            }),
                                                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                                                size: "sm",
                                                                                variant: "secondary",
                                                                                onClick: ()=>setEditingLine(undefined),
                                                                                disabled: saveLine.isPending,
                                                                                "data-testid": Testids.optionalTasks.cancelExerciseLine,
                                                                                children: "Cancel"
                                                                            })
                                                                        ]
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-muted-foreground",
                                                                children: [
                                                                    /*#__PURE__*/ _jsxs("span", {
                                                                        children: [
                                                                            "Qty ",
                                                                            c.quantity > 0 ? qty(c.quantity) : '—',
                                                                            c.unitPrice > 0 ? ` × ${money(c.unitPrice)}` : ''
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("span", {
                                                                        children: [
                                                                            "Funded ",
                                                                            money(c.fundedAmount)
                                                                        ]
                                                                    }),
                                                                    /*#__PURE__*/ _jsxs("span", {
                                                                        children: [
                                                                            "Period ",
                                                                            dateStr(c.popStartDate),
                                                                            " – ",
                                                                            dateStr(c.popEndDate)
                                                                        ]
                                                                    }),
                                                                    lineNowEnds(e, c) && /*#__PURE__*/ _jsxs("span", {
                                                                        "data-testid": Testids.optionalTasks.exerciseContributorNowEnds,
                                                                        children: [
                                                                            "Now ends ",
                                                                            formatContractDate(lineNowEnds(e, c))
                                                                        ]
                                                                    })
                                                                ]
                                                            }),
                                                            c.description && /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs text-muted-foreground",
                                                                children: c.description
                                                            })
                                                        ]
                                                    }, c.id))
                                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-muted-foreground",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                children: [
                                                                    "Exercised ",
                                                                    dateStr(e.exercisedAt)
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                children: [
                                                                    "Qty ",
                                                                    e.quantity > 0 ? qty(e.quantity) : '—',
                                                                    e.unitPrice > 0 ? ` × ${money(e.unitPrice)}` : ''
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                children: [
                                                                    "Funded ",
                                                                    money(e.cost)
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                children: [
                                                                    "Period ",
                                                                    dateStr(e.popStartDate),
                                                                    " – ",
                                                                    dateStr(e.popEndDate)
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    e.description && /*#__PURE__*/ _jsx("div", {
                                                        className: "text-muted-foreground",
                                                        children: e.description
                                                    }),
                                                    e.changeSetId && /*#__PURE__*/ _jsx("div", {
                                                        className: "text-[length:var(--fs-ui-micro)] uppercase tracking-wide text-muted-foreground",
                                                        children: "via modification"
                                                    })
                                                ]
                                            })
                                        ]
                                    }, e.id);
                                })
                            })
                        ]
                    })
                ]
            }),
            !isViewOnly && /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: editing ? 'Edit exercise' : 'Record exercise'
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2 space-y-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "ex-slin",
                                        children: "SLIN #"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "ex-slin",
                                        placeholder: "5001-1",
                                        value: slin,
                                        onChange: (e)=>setSlin(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                htmlFor: "ex-qty",
                                                children: "QTY"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "ex-qty",
                                                type: "number",
                                                min: "0",
                                                value: qtyInput,
                                                onChange: (e)=>setQtyInput(e.target.value)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                htmlFor: "ex-funded",
                                                children: "Funded ($)"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "ex-funded",
                                                type: "number",
                                                min: "0",
                                                step: "0.01",
                                                value: funded,
                                                onChange: (e)=>setFunded(e.target.value)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(Label, {
                                                htmlFor: "ex-unit-price",
                                                children: "Unit price ($)"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "ex-unit-price",
                                                type: "number",
                                                min: "0",
                                                step: "0.01",
                                                value: unitPriceInput,
                                                onChange: (e)=>setUnitPriceInput(e.target.value)
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraDateField, {
                                id: "ex-date",
                                label: "Exercised on",
                                value: when,
                                onValueChange: setWhen
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-1 gap-3 sm:grid-cols-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraDateField, {
                                        id: "ex-pop-start",
                                        label: "Period of performance start",
                                        value: popStart,
                                        onValueChange: setPopStart
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraDateField, {
                                        id: "ex-pop-end",
                                        label: "Period of performance end",
                                        value: popEnd,
                                        onValueChange: setPopEnd
                                    })
                                ]
                            }),
                            offerApplyToLines && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                label: `Apply this period to all ${editingLineCount} line${editingLineCount === 1 ? '' : 's'}`,
                                description: "The schedule reads each line's period first. Unticked, only a line whose dates matched this exercise's old period follows; the rest keep their own.",
                                "data-testid": Testids.optionalTasks.applyPopToLines,
                                controlProps: {
                                    checked: applyPopToLines,
                                    onCheckedChange: (v)=>setApplyPopToLines(v === true)
                                }
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Label, {
                                        htmlFor: "ex-note",
                                        children: "Description"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                                        id: "ex-note",
                                        rows: 2,
                                        value: note,
                                        onChange: (e)=>setNote(e.target.value)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>save.mutate(),
                                        disabled: save.isPending || hasNegativeField,
                                        "data-testid": Testids.optionalTasks.recordExerciseSubmit,
                                        children: save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Record exercise'
                                    }),
                                    editing ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        onClick: resetForm,
                                        disabled: save.isPending,
                                        "data-testid": Testids.optionalTasks.cancelEditExercise,
                                        children: "Cancel"
                                    }) : null
                                ]
                            }),
                            hasNegativeField && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "Quantity, funded amount and unit price cannot be negative."
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
// Source Texts line item 1: preview the contract PDF page where the optional
// task exercise appears. Keyed the same way ClinDetailDrawer keys its own
// SourceChip — `{programId}/{clinNumber}` against target type "CLIN" — since
// that is the only target type SP-1 provenance actually carries for optional
// tasks; delivery.source_references has never held an "OptionalTask" target
// type; a chip targeting one (the pre-SAD-339 behavior) could never resolve.
function ContractSourceLineItem({ programId, projectId, clinNumber, onResolvedChange }) {
    const targetObjectId = clinNumber ? `${programId}/${clinNumber}` : '';
    // pollWhileEmpty: SP-1 provenance extraction lands after this drawer can
    // already be open on a freshly-synced program (SAD-339) — a single fetch
    // that races that landing would show "unavailable" forever.
    const { data: refs } = useSourceRefsForTarget({
        targetObjectType: 'CLIN',
        targetObjectId,
        enabled: !!targetObjectId,
        pollWhileEmpty: true
    });
    const [viewerOpen, setViewerOpen] = useState(false);
    const ref = refs?.[0];
    // Resolve the real artifact filename unconditionally (before the no-ref
    // early return) so hook order never depends on that branch.
    const fileNameById = useArtifactNames(ref?.sourceArtifactId ? [
        ref.sourceArtifactId
    ] : []);
    const resolved = !!projectId && !!ref?.sourceArtifactId;
    // Tells the parent whether this line item resolved, so it knows whether the
    // fail-open fallback paragraph is the only thing left to show.
    useEffect(()=>{
        onResolvedChange(resolved);
    }, [
        resolved,
        onResolvedChange
    ]);
    if (!resolved) {
        return /*#__PURE__*/ _jsx("span", {
            className: "text-muted-foreground",
            "data-testid": Testids.optionalTasks.sourceTextUnavailable,
            children: "No source location on file for the contract PDF."
        });
    }
    const span = parseSpan(ref.sourceSpan);
    const openPage = ref.sourcePage > 0 ? ref.sourcePage : span.page ?? 1;
    const filename = fileNameById.get(ref.sourceArtifactId) ?? 'Unknown document';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                className: "text-left font-medium text-primary hover:underline",
                onClick: ()=>setViewerOpen(true),
                "data-testid": Testids.optionalTasks.sourceTextContractPreview,
                children: [
                    "Preview exercise in contract PDF (p.",
                    openPage,
                    ")"
                ]
            }),
            viewerOpen && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: true,
                onClose: ()=>setViewerOpen(false),
                projectId: projectId,
                document: {
                    documentId: ref.sourceArtifactId,
                    filename
                },
                initialPage: openPage,
                highlightBlockId: span.blockId,
                // This drawer's own SpecraOverlayDrawer is a Radix dialog at the
                // same z-50 as a standalone viewer, so a standalone viewer's close
                // button loses hit-testing to the parent's header. Extension mode
                // (z-[60], positioned beside rather than over the parent) is the
                // pattern CLINDetailDrawer/LCATDetailDrawer already use for exactly
                // this nesting. SpecraOverlayDrawer's default width is 'md'
                // (sm:max-w-lg = 512px), not the 768px this drawer's own default
                // targets.
                mode: "extension",
                parentDrawerWidth: 512
            })
        ]
    });
}
